// Pure helpers for HeroForge (docs/10 §2, §3.2, §4.4; docs/17 §4). Rules numbers never live here:
// stats come from `previewHero` (the sim's `derive_stats`), abilities from the wasm catalogue.
// This file holds only the words the creation screen puts around them.
import type { WasmAbility } from '../../core/sim/actions';
import type { Background, CallingDef, Callings, CompanionDef, ItemTierStats, KitItem, Loadout, Origins, PreviewStats } from '../../core/sim/WasmSimBridge';
import { describeAbility } from '../../core/sim/describe';
import { powerLine, rustPower } from '../../core/items/describe';
import { CLASS_NAMES, isGrantedAbility } from '../../core/sim/adapt';

export const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** 10 §3.2 "role in a five", by class index; docs/104 the paladin, the druid and the bard. */
export const CLASS_ROLE = [
  'Front line, pushes, protects', 'Mobility, singles, hidden', 'Areas, control', 'Heals, wards, revive', 'Ranged, marks, traps',
  'Protector: takes blows for friends, aura', 'Shapes the field: thicket, mud, clearings', 'Songs lift the party, build to a Finale',
] as const;

export interface SpeciesInfo { index: number; name: string; passive: string; detail: string }
/** 10 §2 + docs/59: every species is playable (index = the server's species code). `passive` is the
 *  card line; `detail` adds the senses and footing the details sheet spells out. */
export const SPECIES: readonly SpeciesInfo[] = [
  { index: 0, name: 'Human', passive: 'Once a fight, a blow that would drop you leaves you at 1 HP', detail: 'Determined: once a fight, a blow that would drop a human leaves them at 1 HP instead. Sure-footed: the sky and the season never slow a human down.' },
  { index: 1, name: 'Dwarf', passive: '+1 guard', detail: 'Darkvision: sees in the dark. Walks rough ground at one pace.' },
  { index: 2, name: 'Elf', passive: '+1 move; starts every fight hidden', detail: 'Fey step: starts every fight hidden, until they act. Keen-eyed: sees 2 squares farther through fog, storm and snow. Walks thickets at one pace.' },
  { index: 3, name: 'Halfling', passive: '+1 move; never Staggered', detail: 'Slippery: breaks away from a zone of control for nothing. Brave: nothing staggers a halfling.' },
  { index: 4, name: 'Orc', passive: '+1 weapon damage, +2 HP', detail: 'Darkvision: sees in the dark. Walks mud at one pace.' },
  { index: 5, name: 'Tiefling', passive: '+1 spell damage and heals, +2 HP', detail: 'Darkvision: sees in the dark. Walks sand at one pace.' },
  { index: 6, name: 'Dragonborn', passive: 'Dragon Breath: 3 spell damage down a 4-square line (cooldown 3); +2 HP', detail: 'The breath is an Action: a 4-square line that starts from beside you and hits every foe in it. Scaled: +2 HP, and 2 less damage from fire and burning.' },
  { index: 7, name: 'Half-Elf', passive: '+1 HP, +1 weapon damage, +1 spell damage and heals', detail: 'A little of both worlds: every blow and every spell or heal lands 1 harder. Keen-eyed: sees 2 squares farther through fog, storm and snow.' },
  { index: 8, name: 'Gnome', passive: '+1 to the class resource (Stamina, Mana, Zeal…); starts every fight Warded 2', detail: 'Darkvision: sees in the dark. The extra point is whatever the class runs on. Tinker\'s ward: the first 2 damage of every fight is turned aside.' },
  { index: 9, name: 'Birdfolk', passive: 'Glides over rough, mud, sand, snow and thicket; +2 damage on the first blow of every fight', detail: 'Sure-footed, and glides over rough ground, mud, sand, snow and thickets at one pace (but not over deep water, chasms or lava). Watchful: a bird\'s eye sees one square farther. Dive: the first damaging blow of every fight lands 2 harder.' },
];

export const BUILD_OPTIONS = [{ value: 0, label: 'Broad' }, { value: 1, label: 'Slight' }] as const;
export const buildWord = (b: number): string => (b === 1 ? 'slight' : 'broad');

/** 10 §6.1 gear slots by index. */
export const SLOT_NAMES = ['Main hand', 'Off hand', 'Torso', 'Head', 'Cape', 'Trinket', 'Hands', 'Feet', 'Neck', 'Trinket', 'Legs', 'Waist'] as const;

export const isWorn = (k: KitItem): boolean => k.slot !== null && k.slot !== undefined;

const variant = (v: unknown): string => (typeof v === 'string' ? v : v && typeof v === 'object' ? Object.keys(v as object)[0] ?? '' : '');

/** Abilities of one class (`class` is the `ClassId` name, e.g. "Fighter"), by level then id. */
export function classAbilities(all: readonly WasmAbility[], classId: string): WasmAbility[] {
  const want = classId.toLowerCase();
  // docs/119: the abilities levelling brings (300–599) are the path page's, not the class card's
  return all.filter((a) => (a.class ?? '').toLowerCase() === want && !isLevelAbility(a.id)).sort((a, b) => a.level_req - b.level_req || a.id - b.id);
}

/** docs/119: an ability learned or gained by levelling, not part of the base catalogue. */
export const isLevelAbility = (id: number): boolean => id >= 300 && id <= 599;

/**
 * docs/103: what a class always has that no choice gives — its level-1 passives (the rogue's
 * Backstab). Shown on the class card and the forge's preview, and on every hero sheet.
 */
export function classFeatures(all: readonly WasmAbility[], classId: string): WasmAbility[] {
  // docs/104 NC2: a creation choice's grants (ids 100–199) are the calling's, not the class's
  return classAbilities(all, classId).filter((a) => a.level_req <= 1 && a.slot === 'Passive' && !isGrantedAbility(a.id));
}

export interface Availability { available: boolean; reason?: string }

/**
 * Whether a level-1 ability can be used with this kit. Only gear requirements depend on the
 * loadout (`{ OffHand: "Shield" }`, `{ MainHand: … }`); situational ones (`SelfHidden`,
 * `TargetDowned`) are table conditions and never grey a card out.
 */
export function abilityAvailability(a: Pick<WasmAbility, 'requires'>, stats: Pick<PreviewStats, 'has_shield' | 'ranged_weapon'>, kit: readonly KitItem[]): Availability {
  for (const r of a.requires) {
    const kind = variant(r);
    if (kind !== 'OffHand' && kind !== 'MainHand') continue;
    const want = String((r as Record<string, unknown>)[kind] ?? '');
    const hand = kind === 'OffHand' ? 'off hand' : 'main hand';
    let ok: boolean;
    if (kind === 'OffHand' && want === 'Shield') ok = stats.has_shield;
    else if (kind === 'MainHand' && /ranged|bow/i.test(want)) ok = stats.ranged_weapon;
    else ok = kit.some((k) => k.slot === (kind === 'OffHand' ? 1 : 0));
    if (!ok) return { available: false, reason: `needs a ${want.toLowerCase() || 'item'} in the ${hand} — this loadout has none` };
  }
  return { available: true };
}

export const slotWord = (s: WasmAbility['slot']): string => (s === 'Either' ? 'Action or Bonus' : s);

export function rangeWord(a: Pick<WasmAbility, 'range'>): string {
  const m = a.range.max;
  if (typeof m === 'object') return a.range.min > 1 ? `range ${a.range.min}–${m.Tiles}` : `range ${m.Tiles}`;
  if (m === 'SelfOnly') return 'self';
  if (m === 'Reach') return 'reach';
  return 'weapon';
}

/** "cd 2 · 1 Stamina" — or "at will" for free, cooldown-less abilities. */
export function costWord(a: Pick<WasmAbility, 'cooldown' | 'resource_cost' | 'slot'>, resource: string): string {
  if (a.slot === 'Passive') return 'always on';
  const parts: string[] = [];
  if (a.cooldown > 0) parts.push(`cd ${a.cooldown}`);
  if (a.resource_cost > 0) parts.push(`${a.resource_cost} ${resource}`);
  return parts.length ? parts.join(' · ') : 'at will';
}

export const startingGold = (backgroundGold: number, extra = 0): number => backgroundGold + Math.max(0, extra);

/** The backgrounds in the order the forge offers them to class `cls` (its own first, docs/100);
 *  any the order leaves out follow by id. */
export function backgroundsFor(o: Origins, cls: number): Background[] {
  const order = o.background_order?.[cls] ?? [];
  const rank = (id: number) => { const i = order.indexOf(id); return i < 0 ? order.length + id : i; };
  return [...o.backgrounds].sort((a, b) => rank(a.id) - rank(b.id));
}

/** The background picked when class `cls` is chosen: the first it is offered. */
export const defaultBackground = (o: Origins, cls: number): number => backgroundsFor(o, cls)[0]?.id ?? 0;

export const forgedToast = (name: string, classId: string): string => `${name.trim()} the ${cap(classId)} takes up the road.`;

// ── docs/59: the class choice ("calling") ───────────────────────────────────────────────────────

export type CallingKind = 'feat' | 'specialty' | 'patron' | 'companion' | 'spells' | 'oath' | 'circle' | 'instrument';
/** What each class chooses at creation, by class index (fighter, rogue, wizard, cleric, ranger,
 *  and docs/104 the paladin's oath, the druid's circle, the bard's instrument). */
export const CALLING_KIND: readonly CallingKind[] = ['feat', 'specialty', 'spells', 'patron', 'companion', 'oath', 'circle', 'instrument'];
export const CALLING_HEAD: Record<CallingKind, { label: string; hint: string }> = {
  feat: { label: 'Feat', hint: 'One trick of the trade the Fighter has made their own.' },
  specialty: { label: 'Specialty', hint: 'What this Rogue is best at.' },
  patron: { label: 'Patron god', hint: 'Whom the Cleric prays to: a blessing and a prayer of their own.' },
  companion: { label: 'Animal companion', hint: 'A beast that fights beside the Ranger. You name it.' },
  spells: { label: 'Spells', hint: 'Spark is always known; learn three more.' },
  oath: { label: 'Oath', hint: 'What the Paladin has sworn: an aura on every friend beside them.' },
  circle: { label: 'Circle', hint: 'The Druid’s circle: one spell of the land and a gift of its own.' },
  instrument: { label: 'Instrument', hint: 'What the Bard plays: a song of its own, and its own sound.' },
};

/** The `fk_grammar::ClassId` name of class index `cls` ("Fighter" … "Bard"). */
export const className = (cls: number): string => cap(CLASS_NAMES[cls] ?? '');

/** The callings offered to one class (empty for the wizard, who picks spells instead), by id. */
export function callingsFor(c: Pick<Callings, 'callings'>, cls: number): CallingDef[] {
  const want = CALLING_KIND[cls] === 'spells' ? '' : className(cls);
  return want ? c.callings.filter((d) => d.class === want).sort((a, b) => a.id - b.id) : [];
}

export interface CallingChoice { pick: number; spells: number[]; petName: string; petCoat: number }

/** A class's default: its first calling (the animal's own name for a companion), or the
 *  ruleset's default three spells for a wizard. Every default forges as is. */
export function defaultCalling(c: Callings, cls: number): CallingChoice {
  if (CALLING_KIND[cls] === 'spells') return { pick: 0, spells: [...c.default_wizard_spells], petName: '', petCoat: 0 };
  const first = callingsFor(c, cls)[0];
  return { pick: first?.id ?? 0, spells: [], petName: CALLING_KIND[cls] === 'companion' ? first?.name ?? '' : '', petCoat: 0 };
}

/** A forged hero's choices, to open the forge on them again (docs/82 LB1). A hero with no
 *  origin row took the starter kit (background 0, loadout 0); one with no calling row the default. */
export interface ForgeSeed { name: string; cls: number; species: number; build: number; voice: number; background: number; loadout: number; choice: CallingChoice }

export function seedOf(c: Callings, hero: { name: string; class: number; species: number; build: number }, origin?: { background: number; loadout: number },
  calling?: { pick: number; spells: number[]; petName: string; petCoat: number }, voice?: number): ForgeSeed {
  return {
    name: hero.name, cls: hero.class, species: hero.species, build: hero.build, voice: voice ?? 0,
    background: origin?.background ?? 0, loadout: origin?.loadout ?? 0,
    choice: calling ? { pick: calling.pick, spells: [...calling.spells], petName: calling.petName, petCoat: calling.petCoat } : defaultCalling(c, hero.class),
  };
}

/** Toggle one spell in a wizard's choice; a full hand only takes a spell out. */
export function toggleSpell(chosen: readonly number[], id: number, max: number): number[] {
  if (chosen.includes(id)) return chosen.filter((s) => s !== id);
  return chosen.length >= max ? [...chosen] : [...chosen, id];
}

/** The `forge_hero_called` arguments for a choice (docs/59 CC7). */
export function calledArgs(cls: number, choice: CallingChoice): CallingChoice {
  const kind = CALLING_KIND[cls];
  return {
    pick: kind === 'spells' ? 0 : choice.pick,
    spells: kind === 'spells' ? [...choice.spells] : [],
    petName: kind === 'companion' ? choice.petName.trim() : '',
    petCoat: kind === 'companion' ? choice.petCoat : 0,
  };
}

/** Why the calling stops the Forge button, or undefined when it is complete. */
export function callingBlock(c: Callings, cls: number, choice: CallingChoice): string | undefined {
  const kind = CALLING_KIND[cls];
  if (kind === 'spells') {
    const n = choice.spells.length;
    return n === c.wizard_picks ? undefined : `choose exactly ${c.wizard_picks} spells (${n} chosen)`;
  }
  if (!callingsFor(c, cls).some((d) => d.id === choice.pick)) return `choose a ${CALLING_HEAD[kind!].label.toLowerCase()}`;
  if (kind === 'companion' && !choice.petName.trim()) return 'name the companion first';
  return undefined;
}

/** The level-1 list the preview shows: the class's level-1 abilities the hero actually has (so a
 *  feat, prayer or spell appears only when chosen), plus a species ability (Dragon Breath). */
export function firstAbilities(all: readonly WasmAbility[], classId: string, has: readonly number[]): WasmAbility[] {
  const ids = new Set(has);
  const own = classAbilities(all, classId).filter((a) => a.level_req <= 1 && ids.has(a.id));
  const species = all.filter((a) => !a.class && isGrantedAbility(a.id) && ids.has(a.id));
  return [...own, ...species];
}

/** Abilities every hero has (Move, Wait…): no class, below the species/companion range. */
export function universalAbilities(all: readonly WasmAbility[], has: readonly number[]): WasmAbility[] {
  const ids = new Set(has);
  return all.filter((a) => !a.class && !isGrantedAbility(a.id) && ids.has(a.id));
}

// ── details sheets ───────────────────────────────────────────────────────────────────────────────

export type DetailsKind = 'background' | 'loadout' | 'species' | 'feat' | 'specialty' | 'patron' | 'companion' | 'spell' | 'oath' | 'circle' | 'instrument';
export interface DetailsTarget { kind: DetailsKind; id: number }
export interface StatLine { label: string; value: string }
export interface ItemDoc { name: string; slot: string; family: string; lines: string[]; use?: string; price?: number; worn: boolean; def: number; tier: number }
export interface AbilityDoc { id: number; name: string; slot: string; range?: string; cost: string; cooldown: string; text: string }
export interface DetailsDoc {
  eyebrow: string; title: string; subtitle?: string; blurb?: string;
  paras: string[]; stats: StatLine[]; items: ItemDoc[]; abilities: AbilityDoc[]; notes: string[];
  /** Oryx creature frame, for a companion's portrait. */
  frame?: number;
}

const SHIELD_NAMES = ['Buckler', 'Round shield', 'Kite shield', 'Tower shield'];
const signed = (n: number): string => (n < 0 ? `−${-n}` : `+${n}`);

/** The serde `ItemFamily` in words: "Sword (weapon)", "Kite shield", "Chain armour", "Spell focus". */
export function familyWord(f: unknown): string {
  const k = variant(f);
  const v = f && typeof f === 'object' ? (f as Record<string, unknown>)[k] : undefined;
  switch (k) {
    case 'Weapon': return `${String(v)} (weapon)`;
    case 'Shield': return SHIELD_NAMES[Number(v)] ?? 'Shield';
    case 'Armour': return `${String(v)} armour`;
    case 'Helmet': return `${String(v)} helmet`;
    case 'Cape': return 'Cape';
    case 'Trinket': return 'Trinket';
    case 'Focus': return 'Spell focus';
    case 'Consumable': return 'Consumable (used up)';
    default: return k || 'Item';
  }
}

/** What Use Item does with a consumable, in words. */
export function useEffectWord(e: unknown, resource = 'class resource'): string | undefined {
  const k = variant(e);
  const n = e && typeof e === 'object' ? Number((e as Record<string, unknown>)[k]) : 0;
  switch (k) {
    case 'Heal': return `Use Item: heals ${n} HP.`;
    case 'Resource': return `Use Item: restores ${n} ${resource}.`;
    case 'Hidden': return `Use Item: hides you for ${n} turn${n === 1 ? '' : 's'}.`;
    case 'Reveal': return 'Use Item: shows every hidden thing in the room.';
    case '': return undefined;
    default: return `Use Item: ${k.toLowerCase()}.`;
  }
}

/** An item's non-zero stat lines ("5 weapon damage", "range 2–8", "+1 guard", "two-handed"). */
export function itemLines(it: Pick<KitItem, 'stats' | 'two_handed' | 'powers'>, resource = 'resource'): string[] {
  const s: Partial<ItemTierStats> = it.stats ?? {};
  const out: string[] = [];
  const n = (k: keyof ItemTierStats): number => Number(s[k] ?? 0);
  if (n('base_dmg')) out.push(`${n('base_dmg')} weapon damage`);
  if (n('range')) out.push(n('min_range') > 0 ? `range ${n('min_range')}–${n('range')}` : `range ${n('range')}`);
  else if (n('reach')) out.push(`reach ${n('reach')}`);
  if (n('guard')) out.push(`${signed(n('guard'))} guard`);
  if (n('hp')) out.push(`${signed(n('hp'))} HP`);
  if (n('move_tiles')) out.push(`${signed(n('move_tiles'))} move`);
  if (n('power')) out.push(`${signed(n('power'))} weapon damage (power)`);
  if (n('focus')) out.push(`${signed(n('focus'))} spell damage and heals (focus)`);
  if (n('resource_max')) out.push(`${signed(n('resource_max'))} ${resource}`);
  if (n('guard_pierce')) out.push(`ignores ${n('guard_pierce')} guard`);
  if (n('push_resist')) out.push(`${signed(n('push_resist'))} against shoves`);
  if (it.two_handed) out.push('two-handed');
  for (const p of it.powers ?? []) out.push(powerLine(rustPower(p)));
  return out;
}

/** Lines a family adds that are not stats: a shield is what Shield Bash needs. */
function familyLines(f: unknown): string[] {
  return variant(f) === 'Shield' ? ['a shield in the off hand (Shield Bash needs one)'] : [];
}

export function itemDoc(it: KitItem, resource?: string): ItemDoc {
  const worn = isWorn(it);
  return {
    name: it.name, slot: worn ? SLOT_NAMES[it.slot!] ?? 'Worn' : 'In the bag', family: familyWord(it.family),
    lines: [...itemLines(it, resource), ...familyLines(it.family)], use: useEffectWord(it.use_effect, resource), price: it.price, worn, def: it.def, tier: it.tier,
  };
}

/** A calling's flat stat lines (non-zero only). */
export function callingStatLines(st: CallingDef['stats'], resource = 'resource'): StatLine[] {
  const out: StatLine[] = [];
  const add = (n: number, label: string) => { if (n) out.push({ label, value: signed(n) }); };
  add(st.hp, 'HP'); add(st.guard, 'Guard'); add(st.move_tiles, 'Move'); add(st.power, 'Power');
  add(st.focus, 'Focus'); add(st.resource_max, resource);
  return out;
}

const SLOT_DOC: Record<WasmAbility['slot'], string> = { Action: 'Action', Bonus: 'Bonus action', Either: 'Action or Bonus', Passive: 'Always on' };

export function abilityDoc(a: WasmAbility, resource = 'resource'): AbilityDoc {
  const passive = a.slot === 'Passive';
  return {
    id: a.id, name: a.name, slot: SLOT_DOC[a.slot], range: passive ? undefined : rangeWord(a),
    cost: passive ? 'free' : a.resource_cost > 0 ? `${a.resource_cost} ${resource}` : 'free',
    cooldown: passive ? 'none' : a.cooldown > 0 ? `${a.cooldown} turn${a.cooldown === 1 ? '' : 's'}` : 'none',
    text: describeAbility(a),
  };
}

const CLASS_RESOURCE = ['Stamina', 'Edge', 'Mana', 'Favour', 'Focus', 'Zeal', 'Verdance', 'Tempo'] as const;
export const classResource = (cls: number): string => CLASS_RESOURCE[cls] ?? 'resource';

/** docs/104 NC3: how a class's resource fills, as the forge's stat says it ("+1 / turn"). Zeal and
 *  Tempo never refill on their own: blows taken and songs played fill them. */
export function resourceFill(cls: number, regen: number): string {
  if (cls === 5) return 'from blows on you or beside you';
  if (cls === 7) return '+1 a song';
  if (cls === 6) return `+${regen} / turn, more on living ground`;
  return `+${regen} / turn`;
}

export interface DetailsContext {
  callings: Callings; catalogue: readonly WasmAbility[];
  backgrounds: readonly Background[]; loadouts: readonly Loadout[];
  /** The class index the forge has chosen (its resource names costs). */
  cls: number;
}

const empty = (eyebrow: string, title: string): DetailsDoc => ({ eyebrow, title, paras: [], stats: [], items: [], abilities: [], notes: [] });

/** Everything a details sheet says about one choice, in words and the catalogue's numbers. */
export function detailsDoc(t: DetailsTarget, ctx: DetailsContext): DetailsDoc | undefined {
  const res = classResource(ctx.cls);
  const ability = (id: number, r = res) => { const a = ctx.catalogue.find((x) => x.id === id); return a ? abilityDoc(a, r) : undefined; };
  switch (t.kind) {
    case 'background': {
      const b = ctx.backgrounds.find((x) => x.id === t.id);
      if (!b) return undefined;
      const d = empty('Background', b.name);
      d.blurb = b.blurb;
      d.paras.push(b.grants);
      d.items = (b.items ?? []).map((it) => itemDoc(it, res));
      d.stats.push({ label: 'Gold', value: String(b.gold) });
      d.notes.push('Worn pieces go on at once; bagged ones are spent with Use Item in a fight.');
      return d;
    }
    case 'loadout': {
      const l = ctx.loadouts.find((x) => x.class === ctx.cls && x.id === t.id);
      if (!l) return undefined;
      const d = empty(`${className(ctx.cls)} loadout`, l.name);
      d.blurb = l.blurb;
      d.items = l.items.map((it) => itemDoc(it, res));
      const worth = l.items.reduce((n, it) => n + (it.price ?? 0), 0);
      if (worth) d.notes.push(`Worth ${worth} gold at a trader.`);
      return d;
    }
    case 'species': {
      const sp = SPECIES[t.id];
      if (!sp) return undefined;
      const d = empty('Species', sp.name);
      d.paras.push(`Passive: ${sp.passive}.`, sp.detail);
      if (sp.index === 6) { const a = ability(100, 'resource'); if (a) d.abilities.push(a); }
      d.notes.push('Applied once at creation, on top of class and kit.');
      return d;
    }
    case 'feat': case 'specialty': case 'patron': case 'oath': case 'circle': case 'instrument': {
      const c = ctx.callings.callings.find((x) => x.id === t.id);
      if (!c) return undefined;
      const d = empty(CALLING_EYEBROW[t.kind], c.name);
      d.subtitle = c.title || undefined;
      d.blurb = c.blurb;
      d.paras.push(c.rules);
      d.stats = callingStatLines(c.stats, res);
      d.abilities = c.grants.map((g) => ability(g)).filter((x): x is AbilityDoc => !!x);
      // docs/104: how each of the new choices plays at the table
      if (t.kind === 'oath') d.notes.push('An aura is always on: it works on whoever stands beside you as each turn ends. From level 4 (Radiant Aura) it reaches two squares.');
      if (t.kind === 'circle') d.notes.push('Wild Heart: you walk thicket and mud at one pace, and Verdance refills 1 more at the end of a turn you stand in or beside thicket, shallows or mud.');
      if (t.kind === 'instrument') d.notes.push('A song: every song you play adds 1 Tempo (Rhythm). At 4 Tempo you can play your Finale.');
      return d;
    }
    case 'companion': {
      const c = ctx.callings.callings.find((x) => x.id === t.id);
      const body = ctx.callings.companions.find((x) => x.calling === t.id);
      if (!c || !body) return undefined;
      const d = empty('Animal companion', c.name);
      d.blurb = c.blurb;
      d.frame = body.frame;
      d.paras.push(c.rules, body.senses);
      d.stats = companionStatLines(body);
      d.abilities.push({ id: 0, name: 'Basic Attack (bite)', slot: 'Action', range: 'reach', cost: 'free', cooldown: 'none', text: `Bites a foe next to it for ${body.bite} damage.` });
      const trick = ability(body.trick, 'resource');
      if (trick) d.abilities.push(trick);
      d.notes.push(
        'You control it in fights like a second hero: it plans its own move and action each turn.',
        `+${ctx.callings.companion_hp_per_level} HP per ranger level.`,
        `If the companion or its ranger is knocked out, the other is frenzied with grief for the rest of the fight: it deals +${ctx.callings.frenzy} damage and takes +${ctx.callings.frenzy} more from foes.`,
      );
      return d;
    }
    case 'spell': {
      const a = ctx.catalogue.find((x) => x.id === t.id);
      if (!a) return undefined;
      const d = empty('Wizard spell', a.name);
      d.abilities.push(abilityDoc(a, 'Mana'));
      if (a.id === 30) d.notes.push('Every wizard knows Spark.');
      return d;
    }
  }
}

export function companionStatLines(b: Pick<CompanionDef, 'hp' | 'guard' | 'move_tiles' | 'bite'>): StatLine[] {
  return [
    { label: 'HP', value: String(b.hp) }, { label: 'Guard', value: String(b.guard) }, { label: 'Move', value: String(b.move_tiles) },
    { label: 'Bite', value: String(b.bite) },
  ];
}

/** "11 HP · guard 0 · move 4 · bite 3" for a companion card. */
export const companionLine = (b: Pick<CompanionDef, 'hp' | 'guard' | 'move_tiles' | 'bite'>): string =>
  `${b.hp} HP · guard ${b.guard} · move ${b.move_tiles} · bite ${b.bite}`;

/** The details sheet's eyebrow for a calling kind. */
const CALLING_EYEBROW: Record<'feat' | 'specialty' | 'patron' | 'oath' | 'circle' | 'instrument', string> = {
  feat: 'Fighter feat', specialty: 'Rogue specialty', patron: 'Patron god', oath: 'Paladin oath', circle: 'Druid circle', instrument: 'Bard instrument',
};

/** The calling kind of a calling id (feats 1–6, specialties 11–14, patrons 21–26, companions 31–38,
 *  docs/104 oaths 41–44, circles 51–54, instruments 61–65). */
export function callingDetailsKind(def: Pick<CallingDef, 'kind'>): DetailsKind {
  switch (def.kind) {
    case 'Feat': return 'feat';
    case 'Specialty': return 'specialty';
    case 'Patron': return 'patron';
    case 'Oath': return 'oath';
    case 'Circle': return 'circle';
    case 'Instrument': return 'instrument';
    default: return 'companion';
  }
}
